import { Cell, flexRender } from "@tanstack/react-table";
import { Property } from "../../../rest/responses/Property";
import { useSortable } from "@dnd-kit/sortable";
import { CSSProperties, HTMLProps, ReactNode } from "react";
import { CSS } from "@dnd-kit/utilities";
import { useDataTableContext } from "../../../contexts/DataTableContext";
import { Accessor, ImageUrl } from "../../../types/types";
import Uprn from "../Uprn/Uprn";
import EpcRatingTile from "../EpcRatingTile/EpcRatingTile";
import EmbeddedCarousel from "./EmbeddedCarousel";
import { getIntegrityIcon } from "./DataIntegrityIcons";

const LABELS_BY_INTEGRITY: Record<number, string> = {
	1: "Assumed",
	2: "Confirmed",
	3: "Cloned",
	4: "EPC",
	5: "Supplied",
	6: "API",
};

const DragAlongCell = ({ cell }: { cell: Cell<Property, unknown> }) => {
	const { displayIntegrity, typesByAccessor } = useDataTableContext()!;
	const { isDragging, setNodeRef, transform } = useSortable({
		id: cell.column.id,
	});

	const style: CSSProperties = {
		opacity: isDragging ? 0.8 : 1,
		position: "relative",
		transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
		transition: "width transform 0.2s ease-in-out",
		width: cell.column.getSize(),
		zIndex: isDragging ? 1 : 0,
		overflowWrap: "anywhere",
	};

	let inner: ReactNode;
	const cellValue = cell.getValue();

	switch (cell.column.columnDef.id) {
		case "uprn":
			inner = <Uprn propertyId={cell.row.original.id}>{cell.getValue() as string}</Uprn>;
			break;
		case "metrics.epc_rating_grade":
			inner = <EpcRatingTile grade={cell.getValue() as string} />;
			break;
		case "image_meta.image_urls":
			inner = <EmbeddedCarousel imageUrls={cell.getValue() as ImageUrl[]} />;
			break;

		default:
			const accessor = cell.column.columnDef.id as Accessor;
			const columnMetadatum = typesByAccessor[accessor];

			if (
				(columnMetadatum && columnMetadatum.datatype === "boolean") ||
				cell.column.columnDef.id === "sap_is_rdsap"
			) {
				inner = cellValue ? "✓" : "";
			} else if (
				columnMetadatum &&
				(columnMetadatum.datatype === "float" || columnMetadatum.datatype === "double")
			) {
				inner = new Intl.NumberFormat("en-GB", { minimumFractionDigits: 2 }).format(Number(cellValue));
			} else {
				inner = flexRender(cell.column.columnDef.cell, cell.getContext());
			}
			break;
	}

	const props: HTMLProps<HTMLTableCellElement> = {
		style,
		ref: setNodeRef,
	};

	const cellString =
		cellValue !== null && typeof cellValue === "object" ? JSON.stringify(cellValue) : String(cellValue);

	if (
		displayIntegrity &&
		cell.row.original["data_integrity.integrity"] &&
		// @ts-ignore TODO: Figure this out please... it's not scalar, but the types are generated
		cell.row.original["data_integrity.integrity"][cell.column.id]
	) {
		// @ts-ignore See above
		// These can be strings or numbers.
		const integrity = Number(cell.row.original["data_integrity.integrity"][cell.column.id]);

		// @ts-ignore not sure why data- attributes aren't allowed, they should be
		props["data-cell-data-integrity"] = integrity;

		props.title = LABELS_BY_INTEGRITY[integrity];

		inner = (
			<div className="integrity-cell-content">
				<div className="integrity-cell-icon">{getIntegrityIcon(integrity)}</div>
				{inner}
			</div>
		);
	} else if (cellString.length > 15 && cell.column.id !== "image_meta.image_urls") {
		// The contents might get cut off, so display on hover too (apart from image URLs which have been requested to
		// not show here).
		props.title = cellString;
	}

	return <td {...props}>{inner}</td>;
};

export default DragAlongCell;
